import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, XCircle, Clock, ArrowRight, ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AnalyseAnnonceForm from '../components/AnalyseAnnonceForm';
import { Button } from '../components/ui/button';
import useCanonical from '../hooks/useCanonical';

const FAQ = [
  {
    q: "Êtes-vous vendeur de bateaux ?",
    r: "Non, ValidatedBoat n'a aucun stock de bateaux à vendre. Je suis conseiller nautique indépendant, rémunéré uniquement par mes clients acheteurs."
  },
  {
    q: "L'analyse remplace-t-elle une expertise ?",
    r: "Non. Il s'agit d'une première analyse à distance basée sur l'annonce, les photos et les documents fournis. Elle ne remplace pas une inspection physique, un diagnostic mécanique ou une expertise maritime officielle."
  },
  {
    q: "Combien de temps pour recevoir mon analyse ?",
    r: "Sous 48h ouvrées maximum après réception de votre demande. Vous êtes contacté selon la préférence indiquée (téléphone, email ou WhatsApp)."
  },
  {
    q: "Le prix de l'analyse est-il déduit si je vais plus loin ?",
    r: "Oui. Si vous décidez de commander ensuite un audit pré-achat ou un accompagnement supérieur, le montant de l'analyse (49 €) est déduit de la prestation suivante."
  },
  {
    q: "Que se passe-t-il si le bateau ne semble pas intéressant ?",
    r: "Vous recevez une recommandation claire et argumentée expliquant les raisons de mes doutes. Vous économisez potentiellement un déplacement inutile."
  },
  {
    q: "Intervenez-vous uniquement à La Rochelle ?",
    r: "L'analyse d'annonce se fait à distance, sans limite géographique. Les prestations physiques (audit sur place, sortie d'eau) sont facturées avec les déplacements associés."
  },
];

export default function AnalyseAnnonce() {
  const siteUrl = 'https://validatedboat.fr';
  const [params] = useSearchParams();
  const initialUrl = params.get('url') || '';
  useCanonical(`${siteUrl}/analyse-annonce-bateau`);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": "Analyse d'annonce ValidatedBoat",
        "provider": { "@type": "ProfessionalService", "name": "ValidatedBoat", "url": siteUrl },
        "areaServed": "France",
        "description": "Analyse d'annonce de bateau d'occasion : vérification du prix, détection des incohérences, points de vigilance, questions à poser au vendeur.",
        "offers": { "@type": "Offer", "price": "49.00", "priceCurrency": "EUR" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": FAQ.map(({ q, r }) => ({
          "@type": "Question",
          "name": q,
          "acceptedAnswer": { "@type": "Answer", "text": r }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Accueil", "item": siteUrl },
          { "@type": "ListItem", "position": 2, "name": "Analyse d'annonce", "item": `${siteUrl}/analyse-annonce-bateau` }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-[#dfddd6]" data-testid="page-analyse-annonce">
      <Helmet>
        <title>Analyse d&apos;annonce de bateau d&apos;occasion | ValidatedBoat</title>
        <meta name="description" content="Vous avez repéré un bateau d'occasion ? Faites analyser son annonce, son prix et ses points de vigilance avant de vous déplacer ou de vous engager." />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-gradient-to-b from-[#302f2c] to-[#1a1c1b]">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <p className="text-[#d9fb06] font-bold uppercase text-sm tracking-widest mb-4">Analyse d&apos;annonce</p>
          <h1 className="font-black text-4xl md:text-6xl leading-tight text-[#dfddd6] mb-6">
            Vous avez repéré un bateau ?<br />
            <span className="text-[#d9fb06]">Faites analyser son annonce avant d&apos;y aller.</span>
          </h1>
          <p className="text-lg md:text-xl text-[#888680] max-w-2xl mx-auto mb-8">
            Prix, incohérences, risques, questions à poser au vendeur. Une analyse indépendante avant de vous déplacer.
          </p>
          <Button asChild data-testid="analyse-scroll-form-btn"
            className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-base font-bold uppercase">
            <a href="#formulaire">Envoyer mon annonce <ArrowRight className="ml-2" size={18} /></a>
          </Button>
        </div>
      </section>

      {/* What's analyzed */}
      <section className="py-16 md:py-20 bg-[#1a1c1b]">
        <div className="max-w-5xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-8">
          <Card title="Ce qui est analysé" icon={<CheckCircle2 className="text-[#d9fb06]" />}
            items={[
              "Cohérence du prix par rapport au marché",
              "Informations manquantes ou incohérentes",
              "Photos et description : signaux d'alerte",
              "Historique du modèle et défauts connus",
              "Questions clés à poser au vendeur",
              "Recommandation sur la suite à donner",
              "Entretien téléphonique de 20 minutes",
            ]} />
          <Card title="Ce qui n'est pas inclus" icon={<XCircle className="text-[#888680]" />}
            items={[
              "Inspection physique du bateau",
              "Diagnostic mécanique complet",
              "Expertise maritime officielle",
              "Sortie d'eau ou plongée coque",
              "Analyse des documents douaniers étrangers",
              "Négociation avec le vendeur (proposé en option)",
            ]} muted />
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-[#302f2c]">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <h2 className="font-black text-3xl md:text-5xl uppercase text-[#d9fb06] mb-12 text-center">Comment ça marche</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: '1', t: "Transmettez l'annonce", d: "Copiez le lien de l'annonce et remplissez le formulaire (5 min)." },
              { n: '2', t: "Je l'analyse", d: `Réponse sous 48h ouvrées avec les points de vigilance identifiés.` },
              { n: '3', t: "Vous décidez", d: "Recommandation claire pour aller plus loin ou passer votre chemin." },
            ].map((s) => (
              <div key={s.n} className="bg-[#1a1c1b] border border-[#3f4816] rounded-2xl p-6">
                <div className="w-12 h-12 rounded-full bg-[#d9fb06] text-[#1a1c1b] font-black text-xl flex items-center justify-center mb-4">{s.n}</div>
                <h3 className="text-lg font-bold text-[#dfddd6] mb-2">{s.t}</h3>
                <p className="text-sm text-[#888680] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-[#888680] mt-8 flex items-center justify-center gap-2">
            <Clock size={14} /> Cette première analyse ne remplace pas une inspection physique complète du bateau.
          </p>
        </div>
      </section>

      {/* Form */}
      <section id="formulaire" className="py-16 md:py-20 bg-[#1a1c1b]">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-black text-3xl md:text-5xl uppercase text-[#d9fb06] mb-3 text-center">
            Envoyez votre annonce
          </h2>
          <p className="text-[#888680] text-center mb-10">Sans engagement · Réponse sous 48h ouvrées</p>
          <div className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-6 md:p-8">
            <AnalyseAnnonceForm initialUrl={initialUrl} />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-[#302f2c]">
        <div className="max-w-3xl mx-auto px-5 md:px-10">
          <h2 className="font-black text-3xl md:text-5xl uppercase text-[#d9fb06] mb-12 text-center">Questions fréquentes</h2>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <details key={i} className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg overflow-hidden group" data-testid={`faq-item-${i}`}>
                <summary className="cursor-pointer p-4 md:p-5 flex items-center justify-between text-[#dfddd6] font-semibold hover:text-[#d9fb06] transition-colors">
                  <span>{item.q}</span>
                  <ChevronDown className="flex-shrink-0 group-open:rotate-180 transition-transform" size={20} />
                </summary>
                <p className="px-4 md:px-5 pb-5 text-[#888680] text-sm leading-relaxed">{item.r}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Complementary services */}
      <section className="py-16 md:py-20 bg-[#1a1c1b]">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-black text-2xl md:text-4xl uppercase text-[#d9fb06] mb-4">Envie d&apos;aller plus loin ?</h2>
          <p className="text-[#888680] mb-8">Retrouvez toutes les prestations d&apos;accompagnement acheteur.</p>
          <Link to="/#pricing">
            <Button className="bg-transparent border-2 border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] rounded-full px-8 py-6 font-bold uppercase text-sm transition-colors">
              Voir les accompagnements
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Card = ({ title, icon, items, muted }) => (
  <div className={`rounded-2xl p-6 md:p-8 border ${muted ? 'bg-[#302f2c] border-[#3f4816]' : 'bg-[#302f2c] border-[#d9fb06]/30'}`}>
    <h3 className="text-xl font-bold text-[#d9fb06] mb-4 flex items-center gap-2">{icon} {title}</h3>
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className={`text-sm ${muted ? 'text-[#888680]' : 'text-[#dfddd6]'} flex items-start gap-2`}>
          <span className={`flex-shrink-0 ${muted ? 'text-[#888680]' : 'text-[#d9fb06]'} mt-0.5`}>•</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);
